import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Handshake,
    Wallet,
    Award,
    ArrowUpRight,
    Loader2,
    QrCode,
    Smartphone,
    Car,
    Sparkles,
    Building2,
    Link2,
    Activity,
    Banknote,
    Share2,
    Megaphone,
    Star,
    Network,
    User as UserIcon,
} from "lucide-react";
import ShareLinkCard from "../components/vermittler/ShareLinkCard";
import ActivityHeatmap from "../components/vermittler/ActivityHeatmap";
import ProfileForm from "../components/vermittler/ProfileForm";
import BankForm from "../components/vermittler/BankForm";
import IdVerificationSection from "../components/vermittler/IdVerificationSection";
import PayoutForm from "../components/vermittler/PayoutForm";
import PayoutHistory from "../components/vermittler/PayoutHistory";
import TopBrands from "../components/vermittler/TopBrands";
import ActivityStats from "../components/vermittler/ActivityStats";
import MarketingStatsPanel from "../components/vermittler/MarketingStatsPanel";
import TransactionCodeCard from "../components/vermittler/TransactionCodeCard";
import CollapsibleCard from "../components/vermittler/CollapsibleCard";
import ManualReferralForm from "../components/vermittler/ManualReferralForm";
import AppDownloadCard from "../components/vermittler/AppDownloadCard";
import PublishedReviewsCard from "../components/vermittler/PublishedReviewsCard";
import EmploymentCard from "../components/vermittler/EmploymentCard";
import VermittlerLeadHintsPanel from "../components/vermittler/VermittlerLeadHintsPanel";
import SmilyReportCard from "../components/vermittler/SmilyReportCard";
import HighlightsOfMonthCard from "../components/vermittler/HighlightsOfMonthCard";
import MonthlyEarningsCard from "../components/vermittler/MonthlyEarningsCard";
import VermittlerNetzwerkCard from "../components/vermittler/VermittlerNetzwerkCard";
import { SocialMediaExportSection, WerbemittelSection, WhitelabelEmbedSection } from "../components/DealerSettings";
import PrivatePanel from "../components/vermittler/PrivatePanel";
import CompanyWorkspacePanel from "../components/vermittler/CompanyWorkspacePanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Handshake as HandshakeIcon, User as UserTabIcon, Briefcase as BriefcaseIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

/**
 * Vermittler / Tippgeber Dashboard.
 *
 * Punkte-Übersicht always visible at the top; everything else lives in
 * collapsible cards so the dashboard stays scannable on mobile and the
 * user can drill into one workflow at a time.
 *
 * Grouping (per user feedback, Feb 2026):
 *  - Punkte (immer sichtbar)
 *  - Empfehlen (Transaktionscode + Empfehlung erfassen + Empfehlungs-Link)
 *  - Aktivität (Stats + Heatmap + Top-Marken + Aktivitäten-Liste)
 *  - Auszahlung (Punkte einlösen + Auszahlungs-Konto + Historie)
 *  - Stammdaten (Profil)
 *  - Identitätsnachweis
 */
const ACTIVITY_LABELS = {
    qr_smilie: { label: "QR-Code Smilie", icon: QrCode, color: "text-amber-700 bg-amber-100" },
    nfc: { label: "NFC-Aktion", icon: Smartphone, color: "text-blue-700 bg-blue-100" },
    referral_vehicle: { label: "Fahrzeug-Vermittlung", icon: Car, color: "text-green-700 bg-green-100" },
    other: { label: "Sonstige Aktion", icon: Sparkles, color: "text-purple-700 bg-purple-100" },
};

export default function VermittlerDashboardPage() {
    const { user, loading: authLoading, refresh } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const rawView = searchParams.get("view");
    const firmaId = searchParams.get("firma");
    const firmaModule = searchParams.get("mod");
    const setView = (v) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("view", v);
            if (v !== "firma") { next.delete("firma"); next.delete("mod"); }
            return next;
        }, { replace: true });
    };
    const [activities, setActivities] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [topBrands, setTopBrands] = useState({ items: [], total_referrals: 0, total_points: 0 });
    const [shareLink, setShareLink] = useState(null);
    const [totals, setTotals] = useState({ awarded: 0, paid_out: 0, pending_payout: 0, available: 0 });
    const [hasConfirmedEmployer, setHasConfirmedEmployer] = useState(false);
    const [confirmedEmployers, setConfirmedEmployers] = useState([]);
    const view = (rawView === "privat" || rawView === "firma" || rawView === "vermittlung")
        ? rawView
        : (confirmedEmployers.length > 0 ? "firma" : "vermittlung");
    const [unreadPrivate, setUnreadPrivate] = useState(0);
    const [relationshipType, setRelationshipType] = useState(user?.relationship_type || "");
    const [payoutMethod, setPayoutMethod] = useState(user?.payout_method || "bank");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user) {
            setRelationshipType(user.relationship_type || "");
            setPayoutMethod(user.payout_method || "bank");
        }
    }, [user]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate("/login?role=vermittler");
            return;
        }
        if (user.role !== "vermittler") {
            navigate("/");
        }
    }, [user, authLoading, navigate]);

    const load = async () => {
        try {
            const [actRes, payRes, brandsRes, shareRes, empRes, msgRes] = await Promise.all([
                api.get("/vermittler/activities"),
                api.get("/vermittler/payouts"),
                api.get("/vermittler/top-brands"),
                api.get("/vermittler/share-link"),
                api.get("/vermittler/employment").catch(() => ({ data: { items: [] } })),
                api.get("/messages/threads").catch(() => ({ data: [] })),
            ]);
            setActivities(actRes.data.items || []);
            setTotals(actRes.data.totals);
            setPayouts(payRes.data.items || []);
            setTopBrands(brandsRes.data || { items: [], total_referrals: 0, total_points: 0 });
            setShareLink(shareRes.data);
            const empItems = empRes.data.items || [];
            setHasConfirmedEmployer(empItems.some((l) => l.status === "confirmed"));
            const seen = new Set();
            setConfirmedEmployers(
                empItems
                    .filter((l) => l.status === "confirmed")
                    .map((l) => ({ dealer_id: l.dealer_id, dealer_name: l.dealer_name, permissions: l.permissions || [] }))
                    .filter((e) => { if (seen.has(e.dealer_id)) return false; seen.add(e.dealer_id); return true; }),
            );
            const threads = Array.isArray(msgRes.data) ? msgRes.data : [];
            setUnreadPrivate(threads.reduce((sum, t) => sum + (t.unread || 0), 0));
        } catch (e) {
            toast.error("Aktivitäten konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (user?.role === "vermittler") load();
    }, [user]);

    if (authLoading || !user || user.role !== "vermittler") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    const transactionCode = shareLink?.short_code || user.referral_short_code;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="vermittler-dashboard">
            {/* Header (always visible) */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <Handshake className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">Vermittler &amp; Verkäufer-Dashboard</h1>
                        <p className="text-sm text-gray-600">
                            Willkommen, <strong>{user.name}</strong> · Punktestand &amp; Aktivitäten.
                        </p>
                    </div>
                </div>
                {user.upgrade_to_b2b_requested ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-bold text-xs" data-testid="upgrade-pending-badge">
                        Upgrade-Antrag in Prüfung
                    </Badge>
                ) : hasConfirmedEmployer ? null : (
                    <UpgradeButton onDone={refresh} />
                )}
            </div>

            <Tabs value={view} onValueChange={setView} className="w-full">
                <TabsList className="bg-white border border-gray-200 rounded-sm p-1 mb-2" data-testid="vermittler-view-tabs">
                    <TabsTrigger value="vermittlung" data-testid="vermittler-tab-vermittlung"><HandshakeIcon className="w-4 h-4 mr-1.5" /> Vermittlung &amp; Verkauf</TabsTrigger>
                    <TabsTrigger value="privat" data-testid="vermittler-tab-privat">
                        <UserTabIcon className="w-4 h-4 mr-1.5" /> Privat
                        {unreadPrivate > 0 && (
                            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-mono-num rounded-full" data-testid="vermittler-privat-badge">{unreadPrivate}</span>
                        )}
                    </TabsTrigger>
                    {confirmedEmployers.length > 0 && (
                        <TabsTrigger value="firma" data-testid="vermittler-tab-firma" className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-[#111827] border-2 border-[#FFCC00] font-bold text-base px-5 data-[state=inactive]:bg-[#FFFBEB]">
                            <BriefcaseIcon className="w-5 h-5 mr-1.5" /> Firma
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="vermittlung" className="space-y-6 mt-0">
            {/* Juni-2026 (User-Wunsch) — „Mein Verdienst diesen Monat" links, App-Download rechts in einer Spalte */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="vermittler-earnings-row">
                <div className="lg:col-span-2">
                    <MonthlyEarningsCard />
                </div>
                <AppDownloadCard />
            </div>

            {/* Feb-2026 — Highlights des Monats */}
            <HighlightsOfMonthCard activities={activities} totals={totals} />

            {/* Juni-2026 (User-Wunsch) — „Empfehlung erfassen" links; rechts Empfehlungs-Link + Transaktionscode */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-testid="vermittler-top-actions">
                <ManualReferralForm highlight />
                <div className="space-y-4">
                    <ShareLinkCard data={shareLink} user={user} onRefresh={refresh} />
                    <TransactionCodeCard shortCode={transactionCode} shareUrl={shareLink?.share_url} />
                </div>
            </div>

            {/* Juni-2026 (User-Wunsch) — „Empfehlen & Marketing" aufgelöst: jeder Unterbereich als eigenständige, ein-/ausklappbare Karte */}
            <CollapsibleCard
                title="Sozialmedia"
                icon={Share2}
                hint="WhatsApp · Instagram · TikTok · Facebook · YouTube · X — Zugänge hinterlegen"
                testId="vermittler-section-sozialmedia"
            >
                <SocialMediaExportSection currentUser={user} hideMasterToggle inlineCredentials />
            </CollapsibleCard>

            <CollapsibleCard
                title="Werbemittel & Tracking"
                icon={Megaphone}
                hint="Banner, Landingpage & Werbung auf twowheels.info"
                testId="vermittler-section-werbemittel"
            >
                <WerbemittelSection currentUser={user} hideQrSmiley />
            </CollapsibleCard>

            <CollapsibleCard
                title="Homepage"
                icon={Link2}
                hint="Inserate per iFrame in Ihre eigene Webseite einbetten"
                testId="vermittler-section-homepage"
            >
                <WhitelabelEmbedSection currentUser={user} />
            </CollapsibleCard>

            <CollapsibleCard
                title="Veröffentlichte Bewertungen"
                icon={Star}
                hint="Ihre öffentlich sichtbaren Kundenbewertungen"
                testId="vermittler-section-bewertungen"
            >
                <PublishedReviewsCard />
            </CollapsibleCard>

            <CollapsibleCard
                title="QR-Smily-Aktion"
                icon={QrCode}
                hint="Smily-QR-Code Reporting & Marketing"
                testId="vermittler-section-smily"
            >
                <SmilyReportCard transactionCode={transactionCode} shareUrl={shareLink?.share_url} />
            </CollapsibleCard>

            <CollapsibleCard
                title="Mein Netzwerk"
                icon={Network}
                hint="Ihre geworbenen Vermittler & deren Aktivität"
                testId="vermittler-section-netzwerk"
            >
                <VermittlerNetzwerkCard />
            </CollapsibleCard>

            <CollapsibleCard
                title="Lead-Hinweise"
                icon={Sparkles}
                hint="Tipps & Signale zur Vermittler-Akquise"
                testId="vermittler-section-leadhints"
            >
                <VermittlerLeadHintsPanel />
            </CollapsibleCard>

            {/* 2) Aktivität — Stats, Heatmap, Top-Marken, Aktivitäten-Liste */}
            <CollapsibleCard
                title="Aktivität"
                icon={Activity}
                hint="Aktivitäts-Heatmap, Top-Marken Bestenliste & alle Aktivitäten"
                testId="vermittler-section-aktivitaet"
            >
                <ActivityStats activities={activities} totals={totals} />
                <MarketingStatsPanel />
                <ActivityHeatmap activities={activities} />
                <TopBrands data={topBrands} />
                <ActivitiesList activities={activities} loading={loading} />
            </CollapsibleCard>

            {/* 3) Auszahlung €Punkte — Punkte einlösen, Auszahlungs-Konto, Historie */}
            <CollapsibleCard
                title="Auszahlung €Punkte"
                icon={Banknote}
                hint="Punkte einlösen, Auszahlungs-Konto pflegen und Historie einsehen"
                testId="vermittler-section-auszahlung"
            >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="vermittler-totals">
                    <PointCard icon={Award} label="Gesamt erhalten" value={totals.awarded} tone="amber" testid="total-awarded" />
                    <PointCard icon={Wallet} label="Verfügbar" value={totals.available} tone="green" testid="total-available" />
                    <PointCard icon={Loader2} label="Auszahlung läuft" value={totals.pending_payout} tone="gray" testid="total-pending" />
                    <PointCard icon={ArrowUpRight} label="Ausgezahlt / verrechnet" value={totals.paid_out} tone="blue" testid="total-paid-out" />
                </div>
                <PayoutForm available={totals.available} onSubmitted={load} payoutMethod={payoutMethod} />
                <BankForm user={user} onMethodChange={setPayoutMethod} />
                <PayoutHistory payouts={payouts} loading={loading} />
            </CollapsibleCard>

            {/* 4) Stammdaten — inkl. Ausweis-Dokumente; Arbeitgeber/Provision nur bei „Arbeite im Zweiradhandel" (User-Wunsch) */}
            <CollapsibleCard
                title="Stammdaten"
                icon={UserIcon}
                hint="Persönliche Daten, Anschrift, Ausweis-Dokumente & Profil-Einstellungen"
                testId="vermittler-section-stammdaten"
            >
                <ProfileForm
                    user={user}
                    onSaved={refresh}
                    onRelationshipChange={setRelationshipType}
                    employerSlot={<><EmploymentCard /></>}
                />
                <div className="mt-4">
                    <IdVerificationSection user={user} onSaved={refresh} />
                </div>
            </CollapsibleCard>
                </TabsContent>

                <TabsContent value="privat" className="mt-2" data-testid="vermittler-privat-content">
                    <PrivatePanel user={user} />
                </TabsContent>

                {confirmedEmployers.length > 0 && (
                    <TabsContent value="firma" className="mt-2" data-testid="vermittler-firma-content">
                        {confirmedEmployers.length > 1 && (
                            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="firma-employer-switcher">
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Arbeitgeber:</span>
                                {confirmedEmployers.map((e) => {
                                    const selectedId = confirmedEmployers.find((x) => x.dealer_id === firmaId)?.dealer_id || confirmedEmployers[0].dealer_id;
                                    const isSel = e.dealer_id === selectedId;
                                    return (
                                        <button
                                            key={e.dealer_id}
                                            onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set("view", "firma"); n.set("firma", e.dealer_id); return n; }, { replace: true })}
                                            className={`px-3 py-1.5 rounded-sm text-sm font-bold border transition-colors ${isSel ? "border-[#FFCC00] bg-[#FFCC00] text-[#111827]" : "border-gray-300 bg-white text-gray-700 hover:border-[#111827]"}`}
                                            data-testid={`firma-employer-${e.dealer_id}`}
                                        >
                                            {e.dealer_name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <CompanyWorkspacePanel employer={confirmedEmployers.find((e) => e.dealer_id === firmaId) || confirmedEmployers[0]} initialModule={firmaModule} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}


function ActivitiesList({ activities, loading }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="vermittler-activities-list">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-display font-bold text-base">Aktivitäten</h3>
                <p className="text-xs text-gray-600">
                    Punktevergabe erfolgt durch das Admin-Team nach erfolgreicher Aktion.
                </p>
            </div>
            {loading ? (
                <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : activities.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm" data-testid="activities-empty">
                    Noch keine Aktivitäten erfasst. Starten Sie eine QR-Code- oder NFC-Aktion, um Punkte zu sammeln.
                </div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {activities.map((a) => {
                        const meta = ACTIVITY_LABELS[a.category] || ACTIVITY_LABELS.other;
                        const Icon = meta.icon;
                        return (
                            <li key={a.id} className="px-6 py-4 flex items-start gap-3" data-testid="activity-row">
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-sm ${meta.color}`}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{meta.label}</span>
                                        {a.reference_id && (
                                            <span className="text-[10px] text-gray-400 font-mono-num">· Ref: {a.reference_id}</span>
                                        )}
                                    </div>
                                    <div className="text-sm font-bold text-[#111827]">{a.title}</div>
                                    {a.description && <div className="text-xs text-gray-600 mt-0.5">{a.description}</div>}
                                    <div className="text-[10px] text-gray-400 mt-1 font-mono-num">{new Date(a.created_at).toLocaleString("de-DE")}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-black font-mono-num text-[#FFCC00]">+{a.points}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-gray-400">Punkte</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}


function PointCard({ icon: Icon, label, value, tone, testid }) {
    const tones = {
        amber: "bg-amber-50 border-amber-300 text-amber-900",
        green: "bg-green-50 border-green-300 text-green-900",
        gray: "bg-gray-50 border-gray-300 text-gray-800",
        blue: "bg-blue-50 border-blue-300 text-blue-900",
    };
    return (
        <div className={`border rounded-sm p-4 ${tones[tone]}`} data-testid={testid}>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold">
                <Icon className="w-3.5 h-3.5" />
                {label}
            </div>
            <div className="text-3xl font-black font-mono-num mt-1">{value}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-70">Punkte</div>
        </div>
    );
}


function UpgradeButton({ onDone }) {
    const [working, setWorking] = useState(false);
    const click = async () => {
        if (!window.confirm("Möchten Sie den Upgrade-Antrag zum B2B-Händler stellen?")) return;
        setWorking(true);
        try {
            await api.post("/vermittler/upgrade-to-b2b");
            toast.success("Antrag eingereicht — das Team prüft Ihren Antrag");
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Antrag fehlgeschlagen");
        } finally {
            setWorking(false);
        }
    };
    return (
        <Button
            onClick={click}
            disabled={working}
            className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
            data-testid="upgrade-to-b2b-btn"
        >
            <Building2 className="w-4 h-4 mr-2" />
            {working ? "Antrag läuft…" : "Upgrade auf B2B-Händler"}
        </Button>
    );
}
